import { Fragment, type ReactNode } from 'react';
import styles from './Brand.module.css';

export interface BrandProps {
  /**
   * The copy's own full stop was absorbed into the mark's dot. The dot then
   * stays in the accessibility tree, so the sentence still ends for screen
   * readers.
   */
  readonly fullStop?: boolean;
}

/**
 * The `washed●` wordmark as an inline token — lowercase Bricolage Grotesque with the green
 * accent dot, sized to inherit the surrounding text. Use everywhere the brand
 * name is spoken in body copy (see `brandify`). Mid-sentence the dot is
 * aria-hidden so screen readers say "washed", not "washed dot" or a false
 * sentence break.
 */
export function Brand({ fullStop = false }: BrandProps = {}) {
  return (
    <span className={styles.brand}>
      washed
      <span className={styles.dot} aria-hidden={fullStop ? undefined : true}>
        .
      </span>
    </span>
  );
}

/**
 * Replace every standalone "Washed"/"washed" in a copy string with the <Brand/>
 * wordmark, returning a ReactNode. `\bwashed\b` deliberately does NOT match
 * inside "washedafrica" (no word boundary between `d` and `a`), so the domain /
 * email stay plain text. Only ever run on visible copy — never on aria-labels,
 * alt text, or meta strings (those keep the plain name).
 */
export function brandify(text: string): ReactNode {
  if (!text.includes('ashed')) return text;
  // The mark already ends in its green dot, so a full stop right after the word
  // is absorbed ("…laveuse Washed." must not render "washed.."). The dot then
  // doubles as that full stop for assistive technology.
  const parts = text.split(/(\bwashed\b\.?)/gi);
  return parts.map((part, i) =>
    /^washed\.?$/i.test(part) ? (
      <Brand key={i} fullStop={part.endsWith('.')} />
    ) : (
      <Fragment key={i}>{part}</Fragment>
    ),
  );
}
